import { ActionIcon, MantineProvider, Tooltip } from '@mantine/core';
import { Icon } from '@repo/foundations';
import dictionary from '@repo/secoda-editor/dictionary';
import Extension from '@repo/secoda-editor/lib/Extension';
import { run } from '@repo/secoda-editor/plugins/BlockMenuTrigger';
import isInCode from '@repo/secoda-editor/queries/isInCode';
import { Toolbar } from '@repo/secoda-editor/types';
import { SecodaTheme } from '@repo/theme/mantine/secodaTheme';
import { InputRule } from 'prosemirror-inputrules';
import type { PluginView } from 'prosemirror-state';
import { Plugin } from 'prosemirror-state';
import type { EditorView } from 'prosemirror-view';
import ReactDOM from 'react-dom';
import { MentionMenu } from '../components/MentionMenu';

export const OPEN_REGEX =
	/(?:^|\s)@(([0-9a-zA-Z_.+-]+((\s[0-9a-zA-Z_.+-]+)*|@[0-9a-zA-Z_.+-]*))|)$/;
export const CLOSE_REGEX =
	/(?:^|\s)@(((([0-9a-zA-Z_.+-]+(\s[0-9a-zA-Z_.+-]+)*)|[^0-9a-zA-Z_.+-]+)\s)|([0-9a-zA-Z_.+-]+(@[0-9a-zA-Z_.+-]*)))\s+($)/;

class MentionButtonView implements PluginView {
	editorView: EditorView;

	buttonTrigger?: HTMLDivElement;

	onOpen: VoidFunction;

	constructor(
		editorView: EditorView,
		showMentionMenuButton: boolean,
		onOpen: VoidFunction
	) {
		this.editorView = editorView;
		this.onOpen = onOpen;

		if (!showMentionMenuButton) {
			return;
		}

		this.buttonTrigger = document.createElement('div');
		this.buttonTrigger.className = 'mention-menu-trigger-wrapper';

		ReactDOM.render(
			<MantineProvider
				theme={{ ...SecodaTheme, colorScheme: 'light' }}
				withGlobalStyles
				withNormalizeCSS
			>
				<Tooltip label="Mention resource">
					<ActionIcon
						data-testid="mention-button"
						size="md"
						id="mention-menu-trigger"
						onClick={() => {
							editorView.dispatch(editorView.state.tr.insertText('@'));
							editorView.focus();
							onOpen();
						}}
					>
						<Icon name="at" color="icon/secondary/default" />
					</ActionIcon>
				</Tooltip>
			</MantineProvider>,
			this.buttonTrigger
		);
	}

	destroy() {
		this.buttonTrigger?.remove();
	}
}

export default class MentionTrigger extends Extension {
	get name() {
		// This must NOT match the `name` in mention menu.
		return 'mentionmenu';
	}

	get plugins() {
		return [
			new Plugin({
				view: (view) => {
					const mentionButtonView = new MentionButtonView(
						view,
						this.options.showMentionMenuButton,
						() => this.editor.openToolbar(Toolbar.Mention, '')
					);
					if (mentionButtonView.buttonTrigger) {
						view.dom.parentNode?.append(
							mentionButtonView.buttonTrigger,
							view.dom
						);
					}
					return mentionButtonView;
				},
				props: {
					handleClick: () => {
						if (this.editor.openedToolbar === Toolbar.Mention) {
							this.editor.closeToolbars(Toolbar.Mention);
						}

						return false;
					},
					handleKeyDown: (view, event) => {
						// Prosemirror input rules are not triggered on backspace, however
						// we need them to be evaluted for the filter trigger to work
						// correctly. This additional handler adds inputrules-like handling.
						if (event.key === 'Backspace') {
							// Timeout ensures that the delete has been handled by prosemirror
							// and any characters removed, before we evaluate the rule.
							setTimeout(() => {
								const { pos } = view.state.selection.$from;
								return run(view, pos, pos, OPEN_REGEX, (state, match) => {
									if (match) {
										this.editor.openToolbar(Toolbar.Mention, match[1]);
									} else if (this.editor.openedToolbar === Toolbar.Mention) {
										this.editor.closeToolbars(Toolbar.Mention);
									}
									return null;
								});
							});
						}

						// If the query is active and we're navigating the block menu then
						// just ignore the key events in the editor itself until we're done
						if (
							event.key === 'Enter' ||
							event.key === 'ArrowUp' ||
							event.key === 'ArrowDown' ||
							event.key === 'Tab'
						) {
							const { pos } = view.state.selection.$from;

							return run(view, pos, pos, OPEN_REGEX, (state, match) =>
								// Just tell Prosemirror we handled it and not to do anything
								match ? true : null
							);
						}

						return false;
					},
				},
			}),
		];
	}

	inputRules() {
		return [
			// Main regex should match only:
			// @word
			new InputRule(OPEN_REGEX, (state, match) => {
				if (
					match &&
					state.selection.$from.parent.type.name === 'paragraph' &&
					!isInCode(state)
				) {
					this.editor.openToolbar(Toolbar.Mention, match[1]);
				}
				return null;
			}),
			new InputRule(CLOSE_REGEX, (state, match) => {
				if (match) {
					if (this.editor.openedToolbar === Toolbar.Mention) {
						this.editor.closeToolbars(Toolbar.Mention);
					}
				}
				return null;
			}),
		];
	}

	widget(): React.ReactElement | null {
		if (this.editor.openedToolbar !== Toolbar.Mention) {
			return null;
		}

		return (
			<MentionMenu
				view={this.editor.view!}
				editor={this.editor}
				commands={this.editor.commands}
				dictionary={dictionary}
				rtl
				search={this.editor.blockMenuSearch ?? ''}
				onClose={() => this.editor.closeToolbars(Toolbar.Mention)}
				onTrackEvent={this.editor.options.onTrackEvent}
				canMentionSecodaAi={!!this.editor.options.canMentionSecodaAi}
			/>
		);
	}
}
